import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from "../components/FireAuth";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

function Assessment() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0); // This should start at 0
  const [timeLeft, setTimeLeft] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isFinished, setIsFinished] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [timerPaused, setTimerPaused] = useState(false);
  const [finalConsent, setFinalConsent] = useState({
    truthful: false,
    understand: false
  });

  // Wrap questions array in useMemo to prevent recreation on each render
  const questions = useMemo(() => [
    // Section 1: Salary & Availability
    {
      section: "Salary & Availability",
      question: "What is your current salary or last salary earned?",
      timeLimit: 60, // 1 minute
      type: "shortAnswer"
    },
    {
      section: "Salary & Availability",
      question: "The initial salary for this role is 100,000 naira. Are you comfortable with this amount? Why or why not?",
      timeLimit: 60, // 1 minute
      type: "shortAnswer"
    },
    {
      section: "Salary & Availability",
      question: "If selected, how soon can you start? What is your notice period (if any)?",
      timeLimit: 60, // 1 minute
      type: "shortAnswer"
    },
    
    // Section 2: Experience & Problem-Solving
    {
      section: "Experience & Problem-Solving",
      question: "Describe your experience in marketing, sales, or customer onboarding. What key achievements have you had in these areas?",
      timeLimit: 120, // 2 minutes
      type: "longAnswer"
    },
    {
      section: "Experience & Problem-Solving",
      question: "Tell us about a time when you had to solve a difficult problem at work. How did you handle it?",
      timeLimit: 120, // 2 minutes
      type: "longAnswer"
    },
    {
      section: "Experience & Problem-Solving",
      question: "Have you worked in a target-driven role before? How did you ensure you met or exceeded your targets?",
      timeLimit: 120, // 2 minutes
      type: "longAnswer"
    },
    
    // Section 3: Marketing & Customer Acquisition
    {
      section: "Marketing & Customer Acquisition",
      question: "What strategies would you use to acquire and onboard new customers?",
      timeLimit: 120, // 2 minutes
      type: "longAnswer"
    },
    {
      section: "Marketing & Customer Acquisition",
      question: "How would you handle a situation where a potential customer is hesitant to onboard with us?",
      timeLimit: 120, // 2 minutes
      type: "longAnswer"
    },
    {
      section: "Marketing & Customer Acquisition",
      question: "If given a limited budget for marketing, how would you allocate resources to maximize customer acquisition?",
      timeLimit: 120, // 2 minutes
      type: "longAnswer"
    },
    
    // Section 4: Brand Partnerships & Relationship Management
    {
      section: "Brand Partnerships & Relationship Management",
      question: "What factors do you consider before forming a brand partnership?",
      timeLimit: 120, // 2 minutes
      type: "longAnswer"
    },
    {
      section: "Brand Partnerships & Relationship Management",
      question: "Describe a time when you successfully built a relationship with a key client or partner. What was your approach?",
      timeLimit: 120, // 2 minutes
      type: "longAnswer"
    },
    
    // Section 5: Team Collaboration & Work Ethics
    {
      section: "Team Collaboration & Work Ethics",
      question: "Describe a time when you had to collaborate with a team to achieve a sales or marketing goal. What was your role?",
      timeLimit: 120, // 2 minutes
      type: "longAnswer"
    },
    {
      section: "Team Collaboration & Work Ethics",
      question: "How do you handle disagreements within a team, especially when working on a marketing campaign or customer onboarding strategy?",
      timeLimit: 120, // 2 minutes
      type: "longAnswer"
    },
    {
      section: "Team Collaboration & Work Ethics",
      question: "Are you willing to work beyond normal hours if needed to close a deal or onboard a major client? Why or why not?",
      timeLimit: 60, // 1 minute
      type: "shortAnswer"
    },
    {
      section: "Team Collaboration & Work Ethics",
      question: "What motivates you to excel in a marketing and sales role?",
      timeLimit: 60, // 1 minute
      type: "shortAnswer"
    },
    
    // Section 6: Technology & Product Sales
    {
      section: "Technology & Product Sales",
      question: "Have you ever sold a tech product before? If yes, what was the product, and how did you approach selling it?",
      timeLimit: 120, // 2 minutes
      type: "longAnswer"
    },
    {
      section: "Technology & Product Sales",
      question: "How familiar are you with tech solutions such as CRM, SaaS products, or digital marketing tools? Have you used any in your previous roles?",
      timeLimit: 120, // 2 minutes
      type: "longAnswer"
    },
    {
      section: "Technology & Product Sales",
      question: "Do you understand the difference between B2B (Business-to-Business) and B2C (Business-to-Consumer) sales? Which one do you have more experience with?",
      timeLimit: 120, // 2 minutes
      type: "longAnswer"
    },
    {
      section: "Technology & Product Sales",
      question: "If asked to sell a software product to a business (B2B), how would you structure your pitch?",
      timeLimit: 120, // 2 minutes
      type: "longAnswer"
    }
  ], []); // Empty dependency array means this will only be created once

  // Group questions by section
  const sections = useMemo(() => {
    const sectionsMap = {};
    questions.forEach((q, index) => {
      if (!sectionsMap[q.section]) {
        sectionsMap[q.section] = [];
      }
      sectionsMap[q.section].push({ ...q, index });
    });
    return sectionsMap;
  }, [questions]);

  // Get unique section names
  const sectionNames = useMemo(() => {
    return Object.keys(sections);
  }, [sections]);

  // Check if user data exists in sessionStorage
  useEffect(() => {
    const storedUserData = sessionStorage.getItem('preAssessmentUserData');
    if (!storedUserData) {
      // Redirect to PreQue component if no user data is found
      navigate('/');
      return;
    }
    
    setUserData(JSON.parse(storedUserData));
    
    // Explicitly set current question to 0 to ensure we start from the first question
    setCurrentQuestion(0);
  }, [navigate]);

  // Reference to the textarea element
  const textareaRef = React.useRef(null);

  // Save current answer before moving to next/previous question
  const saveCurrentAnswer = useCallback(() => {
    if (textareaRef.current) {
      const currentAnswer = textareaRef.current.value || '';
      setAnswers(prevAnswers => ({
        ...prevAnswers,
        [currentQuestion]: currentAnswer
      }));
    }
  }, [currentQuestion]);

  // Update textarea value when question changes
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.value = answers[currentQuestion] || '';
    }
  }, [currentQuestion, answers]);

  // Navigate to specific question
  const goToQuestion = useCallback((questionIndex) => {
    saveCurrentAnswer();
    setCurrentQuestion(questionIndex);
    setTimeLeft(questions[questionIndex].timeLimit);
    setTimerPaused(false);
  }, [questions, saveCurrentAnswer]);

  // Navigate to next question
  const handleNextQuestion = useCallback(() => {
    saveCurrentAnswer();
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prevQuestion => prevQuestion + 1);
      setTimeLeft(questions[currentQuestion + 1].timeLimit);
    } else {
      setIsFinished(true);
    }
  }, [currentQuestion, questions, saveCurrentAnswer]);

  // Navigate to previous question
  const handlePrevQuestion = useCallback(() => {
    if (currentQuestion > 0) {
      saveCurrentAnswer();
      setCurrentQuestion(prevQuestion => prevQuestion - 1);
      setTimeLeft(questions[currentQuestion - 1].timeLimit);
    }
  }, [currentQuestion, questions, saveCurrentAnswer]);

  // Initialize first question timer if userData exists
  useEffect(() => {
    if (userData) {
      // Ensure we start from the first question (index 0)
      setCurrentQuestion(0);
      // Set the timer for the first question
      setTimeLeft(questions[0].timeLimit);
    }
  }, [userData, questions]);

  // Timer effect
  useEffect(() => {
    let timer;
    if (!isFinished && timeLeft > 0 && !timerPaused) {
      timer = setTimeout(() => {
        setTimeLeft(prevTime => prevTime - 1);
      }, 1000);
    } else if (!isFinished && timeLeft === 0 && !timerPaused) {
      // Auto-move to next question when time expires
      handleNextQuestion();
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [timeLeft, isFinished, timerPaused, handleNextQuestion]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleFinalConsentChange = (e) => {
    const { name, checked } = e.target;
    setFinalConsent(prevConsent => ({
      ...prevConsent,
      [name]: checked
    }));
  };

  const getCurrentSection = () => {
    return questions[currentQuestion]?.section || "";
  };

  const toggleTimer = () => {
    setTimerPaused(!timerPaused);
  };

  // Get section progress
  const getSectionProgress = () => {
    const currentSectionName = getCurrentSection();
    const sectionQuestions = sections[currentSectionName];
    const currentSectionIndex = sectionQuestions.findIndex(q => q.index === currentQuestion);
    return {
      current: currentSectionIndex + 1,
      total: sectionQuestions.length
    };
  };

  // Jump to section start
  const jumpToSection = (sectionName) => {
    const sectionQuestions = sections[sectionName];
    if (sectionQuestions && sectionQuestions.length > 0) {
      goToQuestion(sectionQuestions[0].index);
    }
  };

  const submitAssessment = async () => {
    if (!finalConsent.truthful || !finalConsent.understand) {
      setSubmitError("Please agree to both statements before submitting.");
      return;
    }
  
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      
      // Save the current answer before submitting
      saveCurrentAnswer();
      
      // Format answers with questions and sections
      const formattedAnswers = {};
      questions.forEach((q, index) => {
        formattedAnswers[`question_${index + 1}`] = {
          section: q.section,
          question: q.question,
          answer: answers[index] || "No answer provided",
          type: q.type
        };
      });
      
      // Add timestamp and application ID
      const applicationId = `${userData.fullName.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`;
      
      // Prepare data for Firestore
      const submissionData = {
        applicationId: applicationId,
        personalInfo: userData,
        answers: formattedAnswers,
        finalConsent: finalConsent,
        submittedAt: serverTimestamp(),
        status: "submitted" // This allows you to track application status
      };
      
      // Add document to Firestore
      const docRef = await addDoc(collection(db, "PreAssessment"), submissionData);
      console.log("Document written with ID: ", docRef.id);
      
      setIsSubmitting(false);
      window.scrollTo(0, 0);
      // Show success message
      setIsFinished('success');
      // Clear sessionStorage data
      sessionStorage.removeItem('preAssessmentUserData');
    } catch (error) {
      console.error("Error adding document: ", error);
      setSubmitError(`There was an error submitting your assessment: ${error.message}. Please try again.`);
      setIsSubmitting(false);
    }
  };

  // Return loading state or redirect if no user data
  if (!userData) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mx-auto"></div>
          <p className="mt-4 text-gray-700">Loading assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header with user info and progress */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-bold">Pre-Assessment</h1>
              <p className="text-sm opacity-90">Applicant: {userData.fullName}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium">Role: {userData.role}</p>
              {!isFinished && (
                <p className="text-xs opacity-90">Question {currentQuestion + 1} of {questions.length}</p>
              )}
            </div>
          </div>
        </div>

        {isFinished === 'success' ? (
          <div className="text-center py-16 px-6">
            <div className="bg-green-100 rounded-full p-4 w-20 h-20 mx-auto mb-4">
              <svg className="w-12 h-12 text-green-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Assessment Submitted Successfully!</h2>
            <p className="text-lg text-gray-600 mb-8">Thank you for completing the pre-assessment. Your responses have been recorded.</p>
            <div className="bg-blue-50 p-6 rounded-lg max-w-md mx-auto">
              <h3 className="font-semibold text-blue-800 mb-2">Next Steps</h3>
              <p className="text-gray-700">We will review your application and contact you if you are selected for the next stage of the final virtual interview process.</p>
            </div>
          </div>
        ) : isFinished ? (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-8 text-center text-gray-800">Assessment Complete</h2>
            
            <div className="mb-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-bold text-lg mb-4 text-blue-800">Final Confirmation</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="truthful"
                    name="truthful"
                    checked={finalConsent.truthful}
                    onChange={handleFinalConsentChange}
                    className="mt-1 mr-3 h-5 w-5 text-blue-600 rounded"
                  />
                  <label htmlFor="truthful" className="text-gray-700">
                    I confirm that all responses are mine and truthful.
                  </label>
                </div>
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="understand"
                    name="understand"
                    checked={finalConsent.understand}
                    onChange={handleFinalConsentChange}
                    className="mt-1 mr-3 h-5 w-5 text-blue-600 rounded"
                  />
                  <label htmlFor="understand" className="text-gray-700">
                    I understand that successful applicants will be contacted for the next stage of the interview.
                  </label>
                </div>
              </div>
            </div>
            
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-300 text-red-700 rounded-lg">
                <div className="flex">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                  </svg>
                  {submitError}
                </div>
              </div>
            )}
            
            <div className="flex space-x-4">
              <button 
                onClick={() => setIsFinished(false)}
                className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
              >
                Review Answers
              </button>
              <button 
                onClick={submitAssessment} 
                disabled={isSubmitting}
                className="flex-1 py-3 px-4 bg-green-600 text-white rounded-md hover:bg-green-700 transition focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 disabled:bg-green-300 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </span>
                ) : 'Submit Assessment'}
              </button>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {/* Section navigation */}
            <div className="p-4 bg-gray-50">
              <div className="flex items-center space-x-2 overflow-x-auto pb-2 scrollbar-thin">
                {sectionNames.map((sectionName, idx) => (
                  <button
                    key={sectionName}
                    onClick={() => jumpToSection(sectionName)}
                    className={`px-3 py-2 text-sm rounded-full whitespace-nowrap transition ${
                      sectionName === getCurrentSection()
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {idx + 1}. {sectionName}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="p-6">
              {/* Current section info */}
              <div className="mb-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-blue-800">{getCurrentSection()}</h3>
                    <p className="text-sm text-gray-500">
                      Question {getSectionProgress().current} of {getSectionProgress().total} in this section
                    </p>
                  </div>
                  
                  <div className="flex items-center">
                    <button 
                      onClick={toggleTimer}
                      className={`mr-3 px-2 py-1 text-xs rounded ${
                        timerPaused ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {timerPaused ? 'Resume Timer' : 'Pause Timer'}
                    </button>
                    <span className={`font-medium px-3 py-1 rounded-full text-sm ${
                      timeLeft < 20 
                        ? 'bg-red-100 text-red-800' 
                        : timeLeft < 40 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-green-100 text-green-800'
                    }`}>
                      {timerPaused ? '⏸ ' : '⏱ '}
                      {formatTime(timeLeft)}
                    </span>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-2.5 mt-3">
                  <div 
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
                    style={{ width: `${(currentQuestion + 1) / questions.length * 100}%` }}>
                  </div>
                </div>
              </div>
              
              {/* Question */}
              <div className="mb-6 p-6 bg-blue-50 border-l-4 border-blue-600 rounded-lg">
                <h2 className="text-xl font-medium text-gray-800 mb-2">
                  {questions[currentQuestion].question}
                </h2>
                <p className="text-sm text-gray-600">
                  {questions[currentQuestion].type === "shortAnswer" 
                    ? "Short answer question (1 minute)" 
                    : "Long answer question (2 minutes)"}
                </p>
              </div>
              
              {/* Answer textarea - now using ref instead of defaultValue */}
              <textarea 
                ref={textareaRef}
                className="w-full p-4 border border-gray-300 rounded-lg h-48 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-y"
                placeholder="Type your answer here..."
              ></textarea>
              
              {/* Navigation buttons */}
              <div className="flex justify-between mt-6">
                <button 
                  onClick={handlePrevQuestion}
                  disabled={currentQuestion === 0}
                  className="px-5 py-2 border border-gray-300 rounded-md text-gray-700 flex items-center hover:bg-gray-100 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                  Previous
                </button>
                
                <button 
                  onClick={handleNextQuestion}
                  className="px-5 py-2 bg-blue-600 rounded-md text-white flex items-center hover:bg-blue-700 transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                >
                  {currentQuestion < questions.length - 1 ? (
                    <>
                      Next
                      <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </>
                  ) : (
                    <>
                      Finish Assessment
                      <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </>
                  )}
                </button>
              </div>
              
              {/* Question number navigation */}
              <div className="mt-8">
                <p className="text-sm text-gray-500 mb-2">Jump to question:</p>
                <div className="flex flex-wrap gap-2">
                  {questions.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => goToQuestion(idx)}
                      className={`w-8 h-8 flex items-center justify-center rounded-full text-sm transition ${
                        idx === currentQuestion
                          ? 'bg-blue-600 text-white'
                          : answers[idx] 
                            ? 'bg-green-100 text-green-800 border border-green-300' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Assessment;